import { EventEmitter, Inject, Injectable } from "@angular/core";

import { Dates, Client, TaxCard, TaxCardType } from "@salaxy/core";

import { ClientNg } from "../api/ClientNg";
import { SessionService } from "../account/SessionService";
import { IRepository } from "./IRepository";

/* tslint:disable */
/* TODO: Waiting for Go-through */

/**
 * Implements the salaxy repository pattern for Taxcards stored for the current account.
 */
@Injectable()
export class TaxCardRepository implements IRepository<TaxCard> {

    /** List of items in the repository */
    public list: TaxCard[] = [];

    /** The tax card that is currently selected to edit or detail view */
    public current: TaxCard = null;

    /** The latest tax card for the current user in role Worker. */
    public latestTaxCard: TaxCard = null;

    private session: SessionService;

    /** Creates a new instance of TaxCardRepository with dependency injection. */
    constructor(
        @Inject(ClientNg) private client: Client,
    ) { }

    /**
     * Assures the repository initialization:
     * Initialization is done after successful authentication.
     * Typically the call to fetch the repository list, but may fetch other stuff from the server.
     *
     * @param session - The session service that contains information about the authenticated user.
     */
    public init(session: SessionService) {
        this.session = session;
        this.reloadList();
    }

    /**
     * Reloads the list from the server - called e.g. after delete and add new
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public reloadList(callback?: () => any): void {
        this.client.taxcardsApi.getTaxCards().then((taxcards) => {
            this.list = taxcards;
            if (callback) {
                callback();
            }
        });
    }

    public getActiveCardForCurrentUser() {
        // HACK: Dummy code => Make real implementation with proper logic (January with previous year etc).
        return this.list.filter((x) => x.card.forYear === 2017)[0];
    }

    public getList(): TaxCard[] {
        return this.list;
    }

    /**
     * Gets a new blank object with default values, suitable for UI binding.
     * This is a synchronous method - does not go to the server and it is not 100% reliable in that way.
     * However, it shoud provide a basic object, good enough for most views.
     */
    public getBlank(): TaxCard {
        const newCard = {
            worker: null,
            owner: this.session ? this.session.avatar : null,
            card: {
                personalId: null, // TODO: Fix this based on profile
                stepsCardIncomeLimit2: null,
                stepsCardTaxPercent3: null,
                forYear: new Date().getFullYear(),
                date: Dates.getToday(),
                taxPercent: null,
                incomeLimit: null,
                // Tax percentage that is used in calculating the widthholding
                // tax for the part of income above the IncomeLimit.
                taxPercent2: null,
                type: TaxCardType.Example,
                fileUri: null,
                previewUri: null,
            },
            incomeLog: [],
            id: null,
            createdAt: Dates.getToday(),
            updatedAt: Dates.getToday(),
            isReadOnly: null,
        };
        if (this.session && this.session.role === "worker" && this.session.personAccount) {
            newCard.card.personalId = this.session.personAccount.identity.officialId;
            newCard.worker = this.session.personAccount.avatar;
        }
        return newCard;
    }

    /**
     * Sets the current repository item.
     * If not loaded, starts loading from the server to the current.
     * @param id - Identifier of the calculation
     */
    public setCurrentId(id: string) {
        let taxCard;
        if (id != null && this.list.length > 0) {
            taxCard = this.list.filter((x) => x.id === id);
            if (taxCard != null) {
                this.setCurrent(taxCard);
            } else {
                // console.log("Tax card with id ", id, "could not be set as current, as it is not ");
            }
        }

    }

    /**
     * Sets the current calculation
     * @param item - Item to set as current
     */
    public setCurrent(item: TaxCard) {
        this.current = item;
    }

    /**
     * Creates a new item and sets it as current for editing and saving.
     * The item is not yet sent to server and thus it is not in the list, nor does it have an identifier.
     */
    public newCurrent() {
        this.setCurrent(this.getBlank());
    }

    /**
     * Saves changes to the current item.
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public saveCurrent(callback?: () => any): void {
        if (this.current) {
            this.save(this.current, callback);
        } else {
            // console.error("SaveCurrent called when current is not set.");
        }
    }

    /**
     * Adds or updates a given item.
     * Operation is determined based on id: null/''/'new' adds, other string values update.
     * @param item - Item that is updated.
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public save(item: TaxCard, callback?: () => any): void {
        this.client.taxcardsApi.saveTaxCard(item)
        .then((savedObject) => {
            this.setCurrent(savedObject);
            this.reloadList(callback);
        });
    }

    /**
     * Deletes the given item from repository if possible
     * @param id - Identifier of the item to delete
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public delete(id: string, callback?: () => any) {
        this.client.taxcardsApi.deleteTaxCard(id)
            .then(() => {
                this.reloadList(callback);
            });
    }

    /**
     * Gets the latest tax card for given personal id (Finnish HETU or SOTU)
     */
    public getLatestTaxCard(personalId: string, callback?) {
        if (personalId != null) {
            this.client.taxcardsApi.getCurrentTaxCard(personalId).then(
                (latest) => {
                    this.latestTaxCard = latest;
                    if (callback) {
                        callback();
                    }
                },
            );
        }
    }
}
